import { supabase } from './supabase';
import { Tables } from '../types/database.types';

export type Poll = Tables<'polls'>;
export type PollOption = Tables<'poll_options'>;
export type PollPointsConfig = Tables<'poll_points_config'>;

export interface PollWithDetails extends Poll {
  poll_options: (PollOption & {
    contestants?: Tables<'contestants'> | null;
  })[];
  poll_points_config?: PollPointsConfig | null;
  seasons?: Tables<'seasons'> & {
    realities?: Tables<'realities'> | null;
  };
  vote_count?: number;
}

export interface CreatePollData {
  title: string;
  description?: string;
  season_id: string;
  poll_mode: 'manual' | 'scheduled';
  start_date?: string;
  end_date?: string;
  show_results: boolean;
  status?: 'draft' | 'active' | 'closed';
}

export interface CreatePollOptionData {
  contestant_id?: string;
  text?: string;
  order: number;
}

export interface PollStats {
  total_votes: number;
  total_voters: number;
  options_stats: {
    option_id: string;
    option_text: string;
    contestant_name?: string;
    vote_count: number;
    point_total: number;
  }[];
}

class PollsAdminService {
  /**
   * Get all polls with details
   */
  async getAll(): Promise<PollWithDetails[]> {
    const { data, error } = await supabase
      .from('polls')
      .select(`
        *,
        poll_options(
          *,
          contestants(*)
        ),
        poll_points_config(*),
        seasons(
          *,
          realities(*)
        )
      `)
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Add vote count to each poll
    const pollsWithCounts = await Promise.all(
      (data || []).map(async (poll) => {
        const { count } = await supabase
          .from('votes')
          .select('*', { count: 'exact', head: true })
          .eq('poll_id', poll.id);

        return {
          ...poll,
          vote_count: count || 0,
        } as PollWithDetails;
      })
    );

    return pollsWithCounts;
  }

  /**
   * Get polls by season
   */
  async getBySeason(seasonId: string): Promise<PollWithDetails[]> {
    const { data, error } = await supabase
      .from('polls')
      .select(`
        *,
        poll_options(
          *,
          contestants(*)
        ),
        poll_points_config(*),
        seasons(
          *,
          realities(*)
        )
      `)
      .eq('season_id', seasonId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Add vote count to each poll
    const pollsWithCounts = await Promise.all(
      (data || []).map(async (poll) => {
        const { count } = await supabase
          .from('votes')
          .select('*', { count: 'exact', head: true })
          .eq('poll_id', poll.id);

        return {
          ...poll,
          vote_count: count || 0,
        } as PollWithDetails;
      })
    );

    return pollsWithCounts;
  }

  /**
   * Get poll by ID
   */
  async getById(id: string): Promise<PollWithDetails | null> {
    const { data, error } = await supabase
      .from('polls')
      .select(`
        *,
        poll_options(
          *,
          contestants(*)
        ),
        poll_points_config(*),
        seasons(
          *,
          realities(*)
        )
      `)
      .eq('id', id)
      .single();

    if (error) throw error;

    // Add vote count
    const { count } = await supabase
      .from('votes')
      .select('*', { count: 'exact', head: true })
      .eq('poll_id', id);

    return {
      ...data,
      vote_count: count || 0,
    } as PollWithDetails;
  }

  /**
   * Create new poll with options
   */
  async create(
    pollData: CreatePollData,
    options: CreatePollOptionData[],
    pointsConfig?: Partial<Omit<PollPointsConfig, 'id' | 'poll_id' | 'created_at' | 'updated_at'>>
  ): Promise<Poll> {
    // Create poll
    const { data: poll, error: pollError } = await supabase
      .from('polls')
      .insert({
        title: pollData.title,
        description: pollData.description || null,
        season_id: pollData.season_id,
        poll_mode: pollData.poll_mode,
        start_date: pollData.start_date || null,
        end_date: pollData.end_date || null,
        show_results: pollData.show_results,
        status: pollData.status || 'draft',
      })
      .select()
      .single();

    if (pollError) throw pollError;

    // Create options
    const optionsToInsert = options.map((opt) => ({
      poll_id: poll.id,
      contestant_id: opt.contestant_id || null,
      text: opt.text || null,
      order: opt.order,
    }));

    const { error: optionsError } = await supabase
      .from('poll_options')
      .insert(optionsToInsert);

    if (optionsError) {
      // Rollback: delete poll if options failed
      await supabase.from('polls').delete().eq('id', poll.id);
      throw optionsError;
    }

    // Create points config if provided
    if (pointsConfig) {
      await supabase.from('poll_points_config').insert({
        poll_id: poll.id,
        points_per_vote: pointsConfig.points_per_vote || 1,
        max_points_per_user: pointsConfig.max_points_per_user || 10,
        daily_reset: pointsConfig.daily_reset || false,
      });
    }

    return poll;
  }

  /**
   * Update poll
   */
  async update(
    id: string,
    pollData: Partial<CreatePollData>,
    options?: CreatePollOptionData[],
    pointsConfig?: Partial<Omit<PollPointsConfig, 'id' | 'poll_id' | 'created_at' | 'updated_at'>>
  ): Promise<Poll> {
    // Convert empty strings and undefined to null for date fields
    const cleanedPollData = {
      ...pollData,
      start_date: pollData.start_date === '' || pollData.start_date === undefined ? null : pollData.start_date,
      end_date: pollData.end_date === '' || pollData.end_date === undefined ? null : pollData.end_date,
      updated_at: new Date().toISOString(),
    };

    // Update poll
    const { data: poll, error: pollError } = await supabase
      .from('polls')
      .update(cleanedPollData)
      .eq('id', id)
      .select()
      .single();

    if (pollError) throw pollError;

    // Update options if provided
    if (options) {
      // Get existing options
      const { data: existingOptions } = await supabase
        .from('poll_options')
        .select('id, contestant_id, text')
        .eq('poll_id', id);

      // Create a set of new option keys (contestant_id or text)
      const newOptionsSet = new Set(
        options.map(opt => opt.contestant_id || opt.text || '')
      );

      // Find options to delete (exist in DB but not in new selection)
      const optionsToDelete = (existingOptions || []).filter(opt => {
        const key = opt.contestant_id || opt.text || '';
        return !newOptionsSet.has(key);
      });

      // Delete removed options (this will cascade delete their votes)
      if (optionsToDelete.length > 0) {
        const idsToDelete = optionsToDelete.map(opt => opt.id);
        await supabase
          .from('poll_options')
          .delete()
          .in('id', idsToDelete);
      }

      // Find options to add (in new selection but not in DB)
      const existingOptionsSet = new Set(
        (existingOptions || []).map(opt =>
          opt.contestant_id || opt.text || ''
        )
      );

      const newOptions = options.filter(opt => {
        const key = opt.contestant_id || opt.text || '';
        return !existingOptionsSet.has(key);
      });

      // Insert new options
      if (newOptions.length > 0) {
        const optionsToInsert = newOptions.map((opt) => ({
          poll_id: id,
          contestant_id: opt.contestant_id || null,
          text: opt.text || null,
          order: opt.order,
        }));

        const { error: optionsError } = await supabase
          .from('poll_options')
          .insert(optionsToInsert);

        if (optionsError) throw optionsError;
      }
    }

    // Update points config if provided
    if (pointsConfig) {
      const { data: existingConfig } = await supabase
        .from('poll_points_config')
        .select('id')
        .eq('poll_id', id)
        .maybeSingle();

      if (existingConfig) {
        await supabase
          .from('poll_points_config')
          .update({
            ...pointsConfig,
            updated_at: new Date().toISOString(),
          })
          .eq('poll_id', id);
      } else {
        await supabase.from('poll_points_config').insert({
          poll_id: id,
          ...pointsConfig,
        });
      }
    }

    return poll;
  }

  /**
   * Delete poll
   */
  async delete(id: string): Promise<void> {
    // Delete poll (cascade will delete options, votes, points config, and share history)
    const { error } = await supabase.from('polls').delete().eq('id', id);

    if (error) throw error;
  }

  /**
   * Activate poll (draft → active)
   */
  async activate(id: string): Promise<Poll> {
    // Validate poll has options
    const { data: options } = await supabase
      .from('poll_options')
      .select('id')
      .eq('poll_id', id);

    if (!options || options.length < 2) {
      throw new Error('La encuesta debe tener al menos 2 opciones');
    }

    const { data, error } = await supabase
      .from('polls')
      .update({ status: 'active', updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  /**
   * Close poll (active → closed)
   */
  async close(id: string): Promise<Poll> {
    const { data, error } = await supabase
      .from('polls')
      .update({ status: 'closed', updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  /**
   * Get poll statistics
   */
  async getStats(id: string): Promise<PollStats> {
    // Get all votes with option and profile info
    const { data: votes, error } = await supabase
      .from('votes')
      .select(`
        option_id,
        points,
        user_id,
        poll_options(
          id,
          text,
          contestants(full_name)
        )
      `)
      .eq('poll_id', id);

    if (error) throw error;

    // Calculate stats
    const uniqueVoters = new Set(votes?.map((v) => v.user_id) || []);
    const optionsMap = new Map<string, any>();

    votes?.forEach((vote: any) => {
      const optionId = vote.option_id;
      const existing = optionsMap.get(optionId) || {
        option_id: optionId,
        option_text: vote.poll_options?.text || '',
        contestant_name: vote.poll_options?.contestants?.full_name || null,
        vote_count: 0,
        point_total: 0,
      };

      existing.vote_count += 1;
      existing.point_total += vote.points || 0;

      optionsMap.set(optionId, existing);
    });

    return {
      total_votes: votes?.length || 0,
      total_voters: uniqueVoters.size,
      options_stats: Array.from(optionsMap.values()),
    };
  }

  /**
   * Set poll as featured (unfeatured all others first)
   */
  async setFeatured(id: string): Promise<Poll> {
    // Get the poll to know which reality it belongs to
    const { data: pollData, error: pollError } = await supabase
      .from('polls')
      .select(`
        id,
        season_id,
        seasons!inner(
          reality_id
        )
      `)
      .eq('id', id)
      .single();

    if (pollError) throw pollError;
    if (!pollData) throw new Error('Encuesta no encontrada');

    const realityId = (pollData.seasons as any).reality_id;

    // Get all season IDs from this reality
    const { data: seasonsData } = await supabase
      .from('seasons')
      .select('id')
      .eq('reality_id', realityId);

    const seasonIds = seasonsData?.map(s => s.id) || [];

    // Unfeature all polls from the SAME reality only
    if (seasonIds.length > 0) {
      await supabase
        .from('polls')
        .update({ is_featured: false, updated_at: new Date().toISOString() })
        .eq('is_featured', true)
        .in('season_id', seasonIds);
    }

    // Then feature the selected poll
    const { data, error} = await supabase
      .from('polls')
      .update({ is_featured: true, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  /**
   * Replace a contestant with another in a poll (Traición)
   * Transfers all votes from the saved contestant to the new one
   */
  async replaceContestant(
    pollId: string,
    pollOptionId: string,
    newContestantId: string,
    reason?: string
  ): Promise<void> {
    // 1. Get poll info and validate it's active
    const { data: poll, error: pollError } = await supabase
      .from('polls')
      .select('id, status, season_id')
      .eq('id', pollId)
      .single();

    if (pollError) throw pollError;
    if (!poll) throw new Error('Encuesta no encontrada');
    if (poll.status !== 'active') {
      throw new Error('Solo se puede hacer traición en encuestas activas');
    }

    // 2. Get the poll option to replace
    const { data: pollOption, error: optionError } = await supabase
      .from('poll_options')
      .select('id, poll_id, contestant_id, contestants(season_id, full_name)')
      .eq('id', pollOptionId)
      .single();

    if (optionError) throw optionError;
    if (!pollOption) throw new Error('Opción de encuesta no encontrada');
    if (!pollOption.contestant_id) {
      throw new Error('Esta opción no tiene un concursante vinculado');
    }

    // 3. Validate new contestant exists and belongs to same season
    const { data: newContestant, error: contestantError } = await supabase
      .from('contestants')
      .select('id, season_id, full_name')
      .eq('id', newContestantId)
      .single();

    if (contestantError) throw contestantError;
    if (!newContestant) throw new Error('Nuevo concursante no encontrado');

    // @ts-ignore - contestants puede ser null
    const oldSeasonId = pollOption.contestants?.season_id;
    if (oldSeasonId !== newContestant.season_id) {
      throw new Error('El nuevo concursante debe pertenecer a la misma temporada');
    }

    // 4. Check if new contestant is already nominated in this poll
    const { data: existingOption } = await supabase
      .from('poll_options')
      .select('id')
      .eq('poll_id', pollId)
      .eq('contestant_id', newContestantId)
      .maybeSingle();

    if (existingOption) {
      throw new Error(`${newContestant.full_name} ya está nominado en esta encuesta`);
    }

    // 5. Get vote stats for this option (snapshot before change)
    const { data: voteStats } = await supabase
      .from('votes')
      .select('id, points')
      .eq('option_id', pollOptionId);

    const votesCount = voteStats?.length || 0;
    const pointsCount = voteStats?.reduce((sum, v) => sum + (v.points || 0), 0) || 0;

    // 6. Get current user ID for audit trail
    const { data: { user } } = await supabase.auth.getUser();
    const userId = user?.id || null;

    // 7. Update the poll_option with new contestant_id and inheritance note
    const { error: updateError } = await supabase
      .from('poll_options')
      .update({
        contestant_id: newContestantId,
        inheritance_note: reason || null
      })
      .eq('id', pollOptionId);

    if (updateError) throw updateError;

    // 8. Record the betrayal in history
    const { error: betrayalError } = await supabase
      .from('poll_betrayals')
      .insert({
        poll_id: pollId,
        poll_option_id: pollOptionId,
        replaced_contestant_id: pollOption.contestant_id,
        new_contestant_id: newContestantId,
        votes_count: votesCount,
        points_count: pointsCount,
        replaced_by: userId,
        reason: reason || null,
      });

    if (betrayalError) throw betrayalError;

    // Success - votes are automatically transferred because they reference poll_option_id
  }

  /**
   * Mark a poll option as saved (salvado)
   */
  async setSaved(pollOptionId: string): Promise<void> {
    const { error } = await supabase
      .from('poll_options')
      .update({ is_saved: true })
      .eq('id', pollOptionId);

    if (error) throw error;
  }

  /**
   * Remove saved status from a poll option
   */
  async removeSaved(pollOptionId: string): Promise<void> {
    const { error } = await supabase
      .from('poll_options')
      .update({ is_saved: false })
      .eq('id', pollOptionId);

    if (error) throw error;
  }

  /**
   * Duplicate votes from one contestant to another (Duplicar Votos)
   * Creates a new poll option with the target contestant and copies all votes
   * The source contestant remains active and keeps their votes
   */
  async duplicateVotes(
    pollId: string,
    sourceOptionId: string,
    targetContestantId: string,
    reason?: string
  ): Promise<void> {
    // 1. Get poll info and validate it's active
    const { data: poll, error: pollError } = await supabase
      .from('polls')
      .select('id, status, season_id')
      .eq('id', pollId)
      .single();

    if (pollError) throw pollError;
    if (!poll) throw new Error('Encuesta no encontrada');
    if (poll.status !== 'active') {
      throw new Error('Solo se puede duplicar votos en encuestas activas');
    }

    // 2. Get the source poll option
    const { data: sourceOption, error: sourceError } = await supabase
      .from('poll_options')
      .select('id, poll_id, contestant_id, order, contestants(season_id, full_name)')
      .eq('id', sourceOptionId)
      .single();

    if (sourceError) throw sourceError;
    if (!sourceOption) throw new Error('Opción de encuesta no encontrada');
    if (!sourceOption.contestant_id) {
      throw new Error('Esta opción no tiene un concursante vinculado');
    }

    // 3. Validate target contestant exists and belongs to same season
    const { data: targetContestant, error: contestantError } = await supabase
      .from('contestants')
      .select('id, season_id, full_name')
      .eq('id', targetContestantId)
      .single();

    if (contestantError) throw contestantError;
    if (!targetContestant) throw new Error('Candidato destino no encontrado');

    // @ts-ignore - contestants puede ser null
    const sourceSeasonId = sourceOption.contestants?.season_id;
    if (sourceSeasonId !== targetContestant.season_id) {
      throw new Error('El candidato destino debe pertenecer a la misma temporada');
    }

    // 4. Check if target contestant is already nominated in this poll
    const { data: existingOption } = await supabase
      .from('poll_options')
      .select('id')
      .eq('poll_id', pollId)
      .eq('contestant_id', targetContestantId)
      .maybeSingle();

    if (existingOption) {
      throw new Error(`${targetContestant.full_name} ya está nominado en esta encuesta`);
    }

    // 5. Get vote stats from source option
    const { data: sourceVotes } = await supabase
      .from('votes')
      .select('id, user_id, points')
      .eq('option_id', sourceOptionId);

    const votesCount = sourceVotes?.length || 0;
    const pointsCount = sourceVotes?.reduce((sum, v) => sum + (v.points || 0), 0) || 0;

    if (votesCount === 0) {
      throw new Error('El candidato origen no tiene votos para duplicar');
    }

    // 6. Get next order number for new option
    const { data: allOptions } = await supabase
      .from('poll_options')
      .select('order')
      .eq('poll_id', pollId)
      .order('order', { ascending: false })
      .limit(1);

    const nextOrder = (allOptions?.[0]?.order || 0) + 1;

    // 7. Create new poll option for target contestant with inheritance note
    const { data: newOption, error: newOptionError } = await supabase
      .from('poll_options')
      .insert({
        poll_id: pollId,
        contestant_id: targetContestantId,
        order: nextOrder,
        inheritance_note: reason || null,
      })
      .select()
      .single();

    if (newOptionError) throw newOptionError;
    if (!newOption) throw new Error('Error al crear nueva opción de encuesta');

    // 8. Duplicate all votes to the new option using RPC to bypass RLS
    if (votesCount > 0) {
      const { error: votesError } = await supabase.rpc('duplicate_votes_to_option', {
        p_source_option_id: sourceOptionId,
        p_target_option_id: newOption.id,
        p_poll_id: pollId,
      });

      if (votesError) {
        // Rollback: delete new option if votes failed
        await supabase.from('poll_options').delete().eq('id', newOption.id);
        throw votesError;
      }
    }

    // 9. Get current user ID for audit trail
    const { data: { user } } = await supabase.auth.getUser();
    const userId = user?.id || null;

    // 10. Record the duplication in history
    const { error: duplicationError } = await supabase
      .from('poll_vote_duplications')
      .insert({
        poll_id: pollId,
        source_option_id: sourceOptionId,
        source_contestant_id: sourceOption.contestant_id,
        target_option_id: newOption.id,
        target_contestant_id: targetContestantId,
        votes_count: votesCount,
        points_count: pointsCount,
        duplicated_by: userId,
        reason: reason || null,
      });

    if (duplicationError) throw duplicationError;

    // Success - votes are duplicated, source remains active
  }
}

export const pollsAdminService = new PollsAdminService();
